import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { skillKeys, useCompanionSkills, type SkillResultDto, type SkillSessionDto } from '../api/skills';
import { bagKeys } from '../api/bag';
import { companionsKeys } from '../api/companions';
import { forageKeys } from '../api/forage';
import { scheduleRewardRefetch } from '../api/rewardRefetch';
import { markAcquired } from '../bag/bagCache';
import { showToast } from './toastStore';

/**
 * The completion toast for a skill session (skill-craft UI polish, spec §3.6). Mounted ONCE in
 * `AppShell` with the active companion's id, the same way `useAchievementCelebrations` is: the
 * poll `useCompanionSkills` already runs while a session is live is thereby a shell-level observer,
 * so the completion is noticed wherever the player happens to be, not only on the skill screen.
 * react-query dedupes this with the screens' own `useCompanionSkills(companionId)` call — it is one
 * query with two observers, not a second poll.
 *
 * **The diff is over `lastCompleted.sessionId`, with an UNSEALED baseline.** `seen` maps a
 * companion id to the highest completed-session id this process has raised a toast for (or seen
 * at seed time). A companion with no entry is *unseeded* — a genuinely different state from
 * "seeded at 0" — and the first payload to arrive only ever seeds it, raising nothing. That is
 * the same first-load guard the celebration producers use, for the same reason: a fresh mount
 * arriving with a session that completed while the tab was closed must not toast it as if it had
 * just happened. Seeding happens on EVERY first payload, including one whose `lastCompleted` is
 * null (seeded at 0) — otherwise a companion's first-ever completion would be swallowed by the seed
 * itself. Session ids are monotonic (a bigserial), so "greater than seen" cannot false-positive on
 * a refetch.
 *
 * **Why the maps are module-level rather than refs.** `AppShell` is unmounted entirely while a
 * battle or a minigame owns the screen, and a five-minute smelt frequently finishes during one. A
 * ref would re-seed from the post-battle payload and silently swallow exactly that completion.
 *
 * **Never written during render.** Both maps are mutated only inside the effect below (CLAUDE.md
 * Lesson 1: a render can be started, discarded and re-run under `<StrictMode>` or a concurrent
 * render, and nothing it mutated is rolled back). The effect is idempotent under `<StrictMode>`'s
 * double invocation because `seen` is advanced in the same statement that decides to toast: the
 * second invocation finds the id already recorded and does nothing.
 *
 * **Where the verb comes from.** The toast's verb is the past tense of the LABEL's first word —
 * `Chopping Copperwood Log` → `Chopped 3 Copperwood Log`, `Smelt Copper Bar` → `Smelted a Copper
 * Bar` — but `SkillResultDto` carries `kind`, `outputName` and the numbers, never the label or the
 * skill: the label lives on the SESSION row, and the session row is gone from the payload by the
 * time its result appears (the sweep deletes it and writes `result_json` in one transaction). So
 * `lastSession` remembers, per companion, the most recent running session seen (written from the
 * effect whenever `sessions[0]` exists), and its `busyLabel` supplies the verb and its `skillCode`
 * the skill name for the level line when the completion lands. **The two are JOINED on the session
 * id** (`SkillSessionDto.id` === `SkillResultDto.sessionId`), and the memory is read BEFORE the
 * payload's own `sessions[0]` overwrites it: one payload can carry both the NEXT running session
 * and the completion of the previous one (started on another device, or any focus/mount refetch
 * after both happened), and reading the new session's label would print the wrong skill in
 * mandated copy ("Made a Copper Bar · Woodcutting 1 → 2" for a finished smelt). A memory whose id
 * does not match is ignored, never "close enough". With no matching memory — the app mounted after
 * the session finished (the unsealed baseline suppresses the toast then anyway), a session this
 * client never saw run, or a label whose first word is not in the tables — the verb falls back to
 * `Gathered` / `Made` and the level line to `Level n → m` (`SkillResultDto` carries no `skillCode`,
 * so the fallback cannot name the skill — a server follow-up, recorded in the task report).
 */

const GATHER_VERBS: Record<string, string> = {
  Chopping: 'Chopped',
  Mining: 'Mined',
  Casting: 'Cast',
  Foraging: 'Foraged',
};

/** Exported for `world/recipeNames.contentDrift.test.ts`: every pack recipe name must START with
 *  one of these keys, because the land list's chip (`LandActivityList.statusFor`) shows only a
 *  session label's first word and this toast conjugates the same word. Add a verb here AND a
 *  recipe with it, never one without the other. */
export const PROCESS_VERBS: Record<string, string> = {
  Smelt: 'Smelted',
  Forge: 'Forged',
  Spin: 'Spun',
  Sew: 'Sewn',
  Wrap: 'Wrapped',
  Carve: 'Carved',
  Cook: 'Cooked',
  Brew: 'Brewed',
};

/** Reward engine E1: why a repeat queue ended early, in the toast's words. */
const QUEUE_STOPPED: Record<string, string> = {
  inputs: 'out of materials',
  unavailable: 'unavailable',
  busy: 'pet busy',
};

/** companionId → highest `lastCompleted.sessionId` seen. No entry = unseeded (see above). */
const seen = new Map<number, number>();
/** companionId → the most recent running session seen for it — the source of the verb. */
const lastSession = new Map<number, SkillSessionDto>();

/** Drops both maps back to unseeded — tests only (each case needs a clean slate; the maps are
 *  deliberately process-lifetime in production, like the celebration baselines). */
export function _resetForTests(): void {
  seen.clear();
  lastSession.clear();
}

function pastTense(kind: string, label: string | undefined): string {
  const first = label?.trim().split(/\s+/)[0] ?? '';
  return kind === 'gather'
    ? (GATHER_VERBS[first] ?? 'Gathered')
    : (PROCESS_VERBS[first] ?? 'Made');
}

/** Exported for the toast copy to be pinned directly: `Smelted a Copper Bar · Smithing 1 → 2`. */
export function completionText(
  r: SkillResultDto,
  remembered: SkillSessionDto | undefined,
  skillName: string | undefined,
): string {
  const verb = pastTense(r.kind, remembered?.busyLabel);
  const count = r.qty === 1 ? 'a' : String(r.qty);
  const parts = [`${verb} ${count} ${r.outputName}`];
  if (r.levelAfter > r.levelBefore) parts.push(`${skillName ?? 'Level'} ${r.levelBefore} → ${r.levelAfter}`);
  if (r.doubled) parts.push('Doubled!');
  if (r.sideFinds.length) parts.push('Rare find!');
  if (r.queueStopped) parts.push(`queue stopped: ${QUEUE_STOPPED[r.queueStopped] ?? r.queueStopped}`);
  return parts.join(' · ');
}

export function useSkillCompletionToast(companionId: number | undefined): void {
  const queryClient = useQueryClient();
  const { data } = useCompanionSkills(companionId);

  // Keyed on the payload itself, not on `lastCompleted.sessionId`: the seed must happen on the
  // first payload even when that payload's `lastCompleted` is null (an id-keyed effect would not
  // re-run for undefined → undefined, leaving the companion unseeded until its first completion
  // arrived and then seeding on THAT — swallowing it). react-query's structural sharing keeps the
  // reference stable across identical refetches, so this is not a per-poll re-run either.
  useEffect(() => {
    if (companionId == null || !data) return;

    // Read the memory BEFORE this payload's own running session overwrites it — see the doc
    // comment: the completion and the next session can arrive together.
    const priorSession = lastSession.get(companionId);
    const running = data.sessions[0];
    if (running) lastSession.set(companionId, running);

    const completed = data.lastCompleted;
    const prior = seen.get(companionId);
    if (prior === undefined) {
      seen.set(companionId, completed?.sessionId ?? 0);
      return;
    }
    if (!completed || completed.sessionId <= prior) return;
    seen.set(companionId, completed.sessionId);

    // Joined on the session id; a memory of some OTHER session is not used at all.
    const remembered = priorSession?.id === completed.sessionId ? priorSession : undefined;
    const skillName = remembered
      ? data.skills.find((s) => s.code === remembered.skillCode)?.name
      : undefined;

    queryClient.invalidateQueries({ queryKey: bagKeys.all });
    queryClient.invalidateQueries({ queryKey: skillKeys.all });
    queryClient.invalidateQueries({ queryKey: companionsKeys.all });
    // The pin's "What grows here" board (docs/SCREENS.md "Forage screen overhaul"): a gather's
    // side roll is what turns a cell from not-yet to found, and since the 180-minute pull was
    // retired (2026-09-18, RULINGS "Retiring the forage pull") this completion is the ONLY event
    // that can — the retired `useForage` had been the board's only invalidator, so a player
    // looking at the board when the toast fired kept seeing the find as undiscovered.
    queryClient.invalidateQueries({ queryKey: forageKeys.all });
    markAcquired([completed.outputItem, ...completed.sideFinds]);
    showToast(completionText(completed, remembered, skillName));
    // A gather/process completion can complete a daily/quest step, but that reward lands via the
    // outbox AFTER the sweep — the invalidations above are too early to see it.
    scheduleRewardRefetch(queryClient);
  }, [companionId, data, queryClient]);
}
